import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Switch, Route } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { matchPath } from 'react-router';
import _ from 'lodash';
import {
	register,
	completeRegistration,
	initRegistration,
} from '../../../../actions/registerActions';
import { session } from '../../../../helpers/storage';
import {
	ROUTE_REGISTER_INCOMPLETE,
	ROUTE_REGISTER_NAME,
	ROUTE_REGISTER_PRIVACY,
	ROUTE_REGISTER_PASSWORD,
	ROUTE_REGISTER_BIRTHDATE,
	ROUTE_REGISTER_GENDER,
	ROUTE_REGISTER,
	ROUTE_ERROR,
} from '../../../../constants/Routes';
import { ACCOUNT_INCOMPLETE, EMAIL_ALREADY_EXISTS } from '../../../../constants/ErrorTypes';

import RegisterEmail from './RegisterEmail';
import RegisterPassword from './RegisterPassword';
import RegisterIncompleteMessage from './RegisterIncompleteMessage';
import RegisterName from './RegisterName';
import RegisterBirthdate from './RegisterBirthdate';
import RegisterGender from './RegisterGender';
import RegisterPrivacy from './RegisterPrivacy';
import {
	getRemainingFormRoutes,
	normalizeGigyaUserProfile,
	funnelEventsOnboarding,
} from '../../../../helpers/registration';
import queryString from 'query-string';
import { change } from 'redux-form';
import UspList from '../../../common/usp/UspList';
import { RATEPLAN_TIERID_MAP, TIER_IDS } from '../../../../constants/Tiers';
import { getTierRateplanFromUrl, isHboBundleInRedirectUrl } from '../../../../helpers/redirect';
import { useGigya } from '../../../../hooks/useGigya';
import useOnboardingTracking from '../../../../hooks/useOnboardingTracking';
import { initiateTrackStructEvent, getSnowplowDuid } from '../../../analytics/Snowplow';
import { EVT_FUNNEL } from '../../../../constants/SnowplowEvents';
import { getExperiment } from '../../../../helpers/sitespect';
import { SITESPECT_EXPERIMENT_NAMES } from '../../../../constants/Sitespect';

const RegisterFormContainer = ({ history, location }) => {
	const isRegExperiment =
		getExperiment(SITESPECT_EXPERIMENT_NAMES.REGISTER_EXPERIMENT) === 'variant';
	const { pathname, search } = location;
	const email = useSelector(
		(state) => state.authentication.email || state.form.register?.values?.email
	);
	const password = useSelector((state) => state.form.register?.values?.password);
	const isIncomplete = useSelector(
		(state) => state.authentication.errorCode === ACCOUNT_INCOMPLETE
	);
	const isEmailAlreadyExists = useSelector(
		(state) => state.register.errorCode === EMAIL_ALREADY_EXISTS
	);
	const gigyaUserProfile = useSelector((state) => state.authentication.gigyaUser.profile);
	const authRegToken = useSelector((state) => state.authentication.gigyaUser.regToken); // Social registration or incomplete account
	const registerRegToken = useSelector((state) => state.register.regToken); // Completely new account)
	const pending = useSelector((state) => state.register.pending);
	const registerState = useSelector((state) => state.form.register);
	const redirectUrl = useSelector((state) => state.authentication.redirectUrl);
	const tierRateplanSlug = getTierRateplanFromUrl(redirectUrl);
	const tierId = RATEPLAN_TIERID_MAP[tierRateplanSlug];
	const isBundlePreselected =
		tierId === TIER_IDS.HBO_ESSENTIAL ||
		tierId === TIER_IDS.HBO_STANDARD ||
		tierId === TIER_IDS.HBO_PREMIUM ||
		isHboBundleInRedirectUrl(redirectUrl);
	useOnboardingTracking({ tierId });

	const dispatch = useDispatch();
	const { isReady: isGigyaReady } = useGigya();

	const [isTrustedParty, setIsTrustedParty] = useState(false);
	const [profileData, setProfileData] = useState(null);

	useEffect(() => {
		if (!search) {
			return;
		}

		setIsTrustedParty(search.includes('entitlementId'));
	}, [search]);

	useEffect(() => {
		if (!profileData && isGigyaReady) {
			// An authRegToken is acquired after a login with an incomplete account (RTLXL, buienradar, etc).
			// If an authRegToken isn't present a new regToken is automatically requested using gigya's initRegistration.
			if (!authRegToken) {
				dispatch(initRegistration());
			}

			// Merge initial profile state coming from three possible sources; query parameters, gigya and redux
			const preFillData = queryString.parse(search);
			const mergedProfileState = {
				...registerState?.values,
				...normalizeGigyaUserProfile(gigyaUserProfile),
				...preFillData,
				password: isIncomplete ? 'ALREADY_SET_ON_ACCOUNT' : registerState?.values?.password,
			};
			// Prevent prefilling e-mail step when the register flow is entered while the previous attempt caused
			// an 'Email already exists' error
			if (isEmailAlreadyExists) {
				mergedProfileState.email = '';
				mergedProfileState.password = '';
				dispatch(change('register', 'email', ''));
				dispatch(change('register', 'password', ''));
			}

			if (mergedProfileState.email) {
				// If email is prefilled, we need to trigger a change on the email field so redux form can save the email in the authentication reducer
				// which will be used later on in the registration process.
				dispatch(change('register', 'email', mergedProfileState.email));
			}

			setProfileData(mergedProfileState);
			const remainingFormRoutes = getRemainingFormRoutes({
				isPrefilled: preFillData,
				isIncomplete,
				profile: mergedProfileState,
				showAllProfileSteps: isEmailAlreadyExists,
				registerState,
			});

			const firstRoute = remainingFormRoutes[0];
			if (!matchPath(firstRoute, pathname)?.isExact) {
				history.push(firstRoute);
			}
		}
	}, [
		pathname,
		gigyaUserProfile,
		isIncomplete,
		search,
		profileData,
		registerState,
		history,
		authRegToken,
		dispatch,
		isEmailAlreadyExists,
		isGigyaReady,
	]);

	function getNextRoute() {
		const { pathname } = location;
		const remainingFormRoutes = getRemainingFormRoutes({
			isRegExperiment,
			isIncomplete,
			profile: profileData,
			showAllProfileSteps: isEmailAlreadyExists,
		});

		const index = _.findIndex(remainingFormRoutes, (path) => {
			return matchPath(pathname, { path, exact: true }) !== null;
		});
		if (index < remainingFormRoutes.length) {
			return remainingFormRoutes[index + 1];
		}
		return null;
	}

	async function trackFunnelEventsOnboarding() {
		const { pathname } = location;

		const data = funnelEventsOnboarding[pathname.replace(/\/+$/, '')];

		if (!data) {
			return;
		}

		await initiateTrackStructEvent({
			category: EVT_FUNNEL,
			action: data.action,
		});
	}

	async function handleSubmit(userData) {
		let nextRoute;
		nextRoute = getNextRoute();
		await trackFunnelEventsOnboarding();

		if (nextRoute) {
			return history.push(nextRoute);
		}

		const newUserData = { ...profileData, ...userData };
		let regToken = registerRegToken;
		const snowplowDuid = getSnowplowDuid();
		let submitFn = () => dispatch(register(newUserData, regToken, snowplowDuid));

		// Complete registration is slightly different from a normal registration
		if (authRegToken) {
			regToken = authRegToken;
			submitFn = () => dispatch(completeRegistration(newUserData, regToken, snowplowDuid));
		}

		return submitFn(newUserData, regToken).catch(() => {
			history.push(ROUTE_ERROR);
		});
	}

	return (
		<>
			<Switch location={location}>
				<Route
					exact
					path={ROUTE_REGISTER}
					render={(props) => {
						/*
						 * Disable the submit button for only the 1st screen until we receive regToken via initRegistration
						 * Otherwise email submit will trigger container re-mounting (check componentDidMount)
						 * Note: this case doesn't appear on social register flow as we'll definitely have the regToken at this point
						 */
						return (
							<RegisterEmail
								{...props}
								pending={pending}
								onSubmit={handleSubmit}
								tierId={tierId}
								isBundlePreselected={isBundlePreselected}
								isRegExperiment={isRegExperiment}
								/*
								 * If there is a 'vl_registration_email' session variable set, this will automatically
								 * validate and submit the form in the componentDidMount hook of RegisterEmail.js
								 * and subsequently remove the 'vl_registration_email' session variable.
								 */
								initialValues={{
									email: session.get('vl_registration_email') || email,
									password,
								}}
							/>
						);
					}}
				/>
				<Route
					exact
					path={ROUTE_REGISTER_PASSWORD}
					render={(props) => <RegisterPassword {...props} onSubmit={handleSubmit} />}
				/>
				<Route
					exact
					path={ROUTE_REGISTER_INCOMPLETE}
					render={(props) => <RegisterIncompleteMessage {...props} onSubmit={handleSubmit} />}
				/>
				<Route
					exact
					path={ROUTE_REGISTER_NAME}
					render={(props) => <RegisterName {...props} onSubmit={handleSubmit} />}
				/>
				<Route
					exact
					path={ROUTE_REGISTER_BIRTHDATE}
					render={(props) => <RegisterBirthdate {...props} onSubmit={handleSubmit} />}
				/>
				<Route
					exact
					path={ROUTE_REGISTER_GENDER}
					render={(props) => <RegisterGender {...props} onSubmit={handleSubmit} />}
				/>
				<Route
					exact
					path={ROUTE_REGISTER_PRIVACY}
					render={(props) => <RegisterPrivacy {...props} onSubmit={handleSubmit} />}
				/>
			</Switch>
			<UspList isTrustedParty={isTrustedParty} isTvGemist={tierId === TIER_IDS.FREE} />
		</>
	);
};

PropTypes.RegisterFormContainer = {
	register: PropTypes.object.isRequired,
	isIncomplete: PropTypes.bool,
	profile: PropTypes.object,
	regToken: PropTypes.string,
	email: PropTypes.string,
};

export default RegisterFormContainer;
